import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService, ContentZonesService } from '@sm360/widget-commons-core-services';
import {
    AnalyticsService,
    MEDIA_QUERIES,
    TRACKING_TAGS as TAGS,
    ShiftDigitalService,
    AdobeService,
    ADOBE_TAGS,
} from '@sm360/widget-commons-general';
import { Quote, Vehicle } from '@sm360/widget-commons-vehicle';
import { SharingBar } from '@sm360/widget-commons-vdp';

import PaymentDetail from 'commons-vehicle/Vehicle/components/Payment/PaymentDetail/PaymentDetail.jsx';
// import PURCHASE_METHODS from 'commons-vehicle/PurchaseMethods/constants/purchaseMethodsValues.js';
// import PurchaseMethodsConstantV25 from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';

import DrivePowerTrainAndColourSection from 'vdp-used/containers/VdpSections/DrivePowerTrainAndColour.js';

import 'vdp-used/components/IdentificationPriceSection/IdentificationPriceSection.scss';

const { ThirdPartiesCTA } = Vehicle;

const IdentificationPriceSection = ({
    actions,
    optionalBuyCta,
    optionalPriceCta,
    optionalAppointmentCta,
    invalidState,
    quote,
    quotes,
    vehicle,
    purchaseMethods,
    hasSDInitialClickSent,
    onSetSDInitialClickEvent,
    cashPurchaseOption,
    config,
    hasAdobeTagging,
    displayCreditRate,
    motoInsightUsedEnabled,
    roadsterUsedEnabled,
    eshopUsedEnabled,
    shopClickDriveUsedEnabled,
    roadsterLink,
    dealerCode,
    dealerBAC,
}) => {
    const taxCodes = purchaseMethods.getTaxCodesString();
    const isUnavailable = !vehicle.isAvailable();

    const trackBuyOnlineCta = () => {
        AnalyticsService.gaTrigger(TAGS.RESERVE_IT_NOW_CTA_USED(quote.vehicle.getModel()));
        ShiftDigitalService.updateDrInitialClick(quote.id, quote.vehicle);
        if (hasAdobeTagging) {
            AdobeService.eventTrigger(
                ADOBE_TAGS.VDP_ACTIONS(
                    quote?.vehicle?.vehicleCondition,
                    'buy online',
                    {
                        make: quote?.vehicle?.make?.name,
                        model: quote?.vehicle?.model?.name,
                    },
                    vehicle?.commercialVehicle
                ),
                'global-onclick'
            );
        }
    };

    const trackRequireInfoCta = () => {
        if (hasAdobeTagging) {
            AdobeService.eventTrigger(
                ADOBE_TAGS.REQUIRE_INFO_CTA(quote?.vehicle?.vehicleCondition, {
                    make: quote?.vehicle?.make?.name,
                    model: quote?.vehicle?.model?.name,
                    bodyStyle: quote?.vehicle?.bodyStyle?.name,
                }),
                'form-submit'
            );
        }
    };

    const trackBuyOnlineCtaOnEndProcessGaTag = TAGS.RESERVE_IT_NOW_PROCESS_ENDED_USED(quote.vehicle.getModel());

    const trackAppointmentCta = () =>
        AnalyticsService.gaTrigger(TAGS.PREPARE_YOUR_VISIT_CTA_USED(quote.vehicle.getModel()));

    const appointmentCtaOnEndProcessGaTag = TAGS.PREPARE_YOUR_VISIT_PROCESS_ENDED_USED(quote.vehicle.getModel());

    const saveToGarage = () => {
        // Send initial click event if its not already sent
        if (!hasSDInitialClickSent) onSetSDInitialClickEvent();
        actions.saveToGarage(null, config);
    };

    const getCZCustomTagContent = () => {
        return ContentZonesService.getContentZone('widget.listing.used', 'custom.tag.content')
            ? ContentZonesService.getContentZone('widget.listing.used', 'custom.tag.content').getValue()
            : '';
    };

    const roadsterCZCtaLabel = ContentZonesService.getContentZone('widget.vdp.used.actions.cta', 'roadster.cta.label')
        ? ContentZonesService.getContentZone('widget.vdp.used.actions.cta', 'roadster.cta.label').getValue()
        : LocaleService.t('CALL_TO_ACTION.BTN_ROADSTER_TEXT');

    const shopClickDriveCZCtaLabel = ContentZonesService.getContentZone(
        'widget.vdp.used.actions.cta',
        'shop.click.drive.cta.label'
    )
        ? ContentZonesService.getContentZone('widget.vdp.used.actions.cta', 'shop.click.drive.cta.label').getValue()
        : LocaleService.t('CALL_TO_ACTION.BTN_SHOP_CLICK_DRIVE_TEXT');

    // Display Sharing bar on VDP Used
    const displaySharingBar = ContentZonesService.getContentZone('widget.vdp.used', 'show.sharing.bar');

    const getStartedBtn = !isUnavailable && (
        <Vehicle.BuyOnlineCTAV25
            buttonClass="sr-button-1 is-fluid"
            quote={quote}
            alternativeLabel={optionalBuyCta}
            iconClass="icon-shopping-cart"
            onClick={trackBuyOnlineCta}
            onEndProcessGaTag={trackBuyOnlineCtaOnEndProcessGaTag}
        />
    );

    const priceQuoteBtn = !isUnavailable && (
        <Vehicle.PriceQuoteCTAV25
            vehicle={vehicle}
            buttonClass="sr-button-2 is-fluid"
            iconClass="icon-brochure"
            alternativeLabel={optionalPriceCta}
            onClick={trackRequireInfoCta}
        />
    );
    const shouldDisplayViewAllInventoryCta = () => {
        return ContentZonesService.getContentZone('widget.vdp.used.actions.cta', 'should.display.view.all.inventory')
            ? ContentZonesService.getContentZone(
                  'widget.vdp.used.actions.cta',
                  'should.display.view.all.inventory'
              ).getValue()
            : '';
    };

    const viewAllInventoryRedirectionLink = () => {
        return ContentZonesService.getContentZone('widget.vdp.used.actions.cta', 'view.all.inventory.redirection.link')
            ? ContentZonesService.getContentZone(
                  'widget.vdp.used.actions.cta',
                  'view.all.inventory.redirection.link'
              ).getValue()
            : '';
    };

    const viewAllInventoryBtn = shouldDisplayViewAllInventoryCta() !== 'false' &&
        viewAllInventoryRedirectionLink() && (
            <Vehicle.ViewAllInventoryCTA
                vehicle={vehicle}
                buttonClass="sr-button-2 is-fluid"
                redirection={viewAllInventoryRedirectionLink()}
            />
        );

    const carCtaBlock = !invalidState && (
        <div className="car-cta">
            {getStartedBtn}
            {priceQuoteBtn}
            {viewAllInventoryBtn}
            <MediaQuery maxWidth={MEDIA_QUERIES.MOBILE.MAX}>
                <Vehicle.AppointmentCTAV25
                    quote={quote}
                    alternativeLabel={optionalAppointmentCta}
                    iconClass="icon-calendar"
                    onClick={trackAppointmentCta}
                    onEndProcessGaTag={appointmentCtaOnEndProcessGaTag}
                />
            </MediaQuery>
        </div>
    );

    let buttonBarBlock = null;
    if (invalidState) {
        buttonBarBlock = (
            <div className="sr-text is-centered invalid-state-message">
                {LocaleService.t('VDP_USED.ERRORS.INVALID_STATE_MESSAGE')}
            </div>
        );
    } else if (!isUnavailable) {
        buttonBarBlock = (
            <div className="showroom-vdp-used-features-button">
                <Quote.AddToGarageButtonV25
                    quote={quote}
                    garageQuotes={quotes}
                    onClick={saveToGarage}
                    hasAdobeTagging={hasAdobeTagging}
                    vehicle={vehicle}
                />
            </div>
        );
    }

    return (
        <div className="showroom-vdp-used-car-info-wrapper">
            <div className="showroom-vdp-used-car-info">
                <div className="car-detail">
                    {displaySharingBar && displaySharingBar.getValue() === 'true' && <SharingBar />}
                    <Vehicle.TileTag
                        shouldRender={vehicle?.custom && getCZCustomTagContent()}
                        background="#f2bf24"
                        color="#fff"
                        text={getCZCustomTagContent()}
                    />
                    {!!vehicle.vin && (
                        <div className="car-number is-light">
                            {LocaleService.t('VDP_USED.VIN_NUMBER', {
                                vin: vehicle.vin,
                            })}
                        </div>
                    )}
                    {!!vehicle.stockNumber && <div className="car-number is-light">#{vehicle.stockNumber}</div>}
                    <h1 className="car-name sr-title-1 is-centered">{vehicle.getYearMakeModel()}</h1>
                    {!!vehicle.modelTrim && <h3 className="car-trim sr-text is-bold">{vehicle.modelTrim.name}</h3>}
                    {!!vehicle.tagline && <div className="car-options is-util">{vehicle.tagline}</div>}
                </div>
                <PaymentDetail
                    isUsed
                    purchaseMethods={purchaseMethods}
                    taxCodes={taxCodes}
                    // forcedPurchaseMethod={PurchaseMethodsConstantV25.CASH}
                    cashPurchaseOption={cashPurchaseOption}
                    displayCreditRate={displayCreditRate}
                />
                {carCtaBlock}
                <ThirdPartiesCTA
                    motoInsightEnabled={motoInsightUsedEnabled}
                    roadsterEnabled={roadsterUsedEnabled}
                    shopClickDriveEnabled={shopClickDriveUsedEnabled}
                    eshopEnabled={eshopUsedEnabled}
                    roadsterLink={roadsterLink}
                    dealerBAC={dealerBAC}
                    dealerCode={dealerCode}
                    vehicle={vehicle}
                    eshopExtraClass="fullWidth"
                    roadsterCZCtaLabel={roadsterCZCtaLabel}
                    shopClickDriveCZCtaLabel={shopClickDriveCZCtaLabel}
                />
            </div>
            <DrivePowerTrainAndColourSection />
            <div className="car-garage clearfix">{buttonBarBlock}</div>
        </div>
    );
};

IdentificationPriceSection.defaultProps = {
    optionalBuyCta: null,
    optionalPriceCta: null,
    optionalAppointmentCta: null,
    invalidState: false,
    hasSDInitialClickSent: false,
    onSetSDInitialClickEvent: null,
    hasAdobeTagging: false,
    displayCreditRate: false,
    motoInsightUsedEnabled: false,
    roadsterUsedEnabled: false,
    eshopUsedEnabled: false,
    shopClickDriveUsedEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

IdentificationPriceSection.propTypes = {
    actions: PropTypes.shape({
        setQuoteLead: PropTypes.func.isRequired,
        saveToGarage: PropTypes.func.isRequired,
    }).isRequired,
    optionalBuyCta: PropTypes.string,
    optionalPriceCta: PropTypes.string,
    optionalAppointmentCta: PropTypes.string,
    invalidState: PropTypes.bool,
    quote: PropTypes.instanceOf(Quote.QuoteModelV25).isRequired,
    quotes: PropTypes.arrayOf(PropTypes.instanceOf(Quote.QuoteModelV25)),
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModelV25).isRequired,
    hasSDInitialClickSent: PropTypes.bool,
    onSetSDInitialClickEvent: PropTypes.func,
    hasAdobeTagging: PropTypes.bool,
    displayCreditRate: PropTypes.bool,
    motoInsightUsedEnabled: PropTypes.bool,
    roadsterUsedEnabled: PropTypes.bool,
    eshopUsedEnabled: PropTypes.bool,
    shopClickDriveUsedEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

IdentificationPriceSection.displayName = 'IdentificationPriceSection';

export default IdentificationPriceSection;
